import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { Link } from "@inertiajs/react";
import { AnimatedBeam } from "../../components/magicui/animated-beam";
import { Iphone } from "../../components/magicui/iphone";
import { MagicCard } from "../../components/magicui/magic-card";
import { Marquee } from "../../components/magicui/marquee";
import { PublicNavbar } from "../../components/public/public-navbar";
import { ShimmerButton } from "../../components/magicui/shimmer-button";
import { cn } from "../../lib/utils";

const VIDEO_SOURCES = [
  "/media/home/ugc-1.mp4",
  "/media/home/ugc-2.mp4",
  "/media/home/ugc-3.mp4"
];

const HERO_TILES = Array.from({ length: 9 }, (_, index) => ({
  id: index + 1,
  src: VIDEO_SOURCES[index % VIDEO_SOURCES.length]
}));

const SOCIAL_PLATFORMS = ["Facebook", "Instagram", "TikTok", "LinkedIn"];

const INFLUENCER_GRID_IMAGES = [
  "/media/influencer-grid/office-01.jpg",
  "/media/influencer-grid/office-02.jpg",
  "/media/influencer-grid/office-03.jpg",
  "/media/influencer-grid/office-04.jpg",
  "/media/influencer-grid/office-05.jpg",
  "/media/influencer-grid/office-06.jpg",
  "/media/influencer-grid/office-07.jpg",
  "/media/influencer-grid/office-08.jpg"
];

const RESULT_STATS = [
  {
    value: "+87%",
    label: "ROI",
    description: "Brands see higher returns by creating and testing ads faster with our AI ad generator."
  },
  {
    value: "10x",
    label: "Ad Variations",
    description: "AI enables rapid testing of multiple creatives at scale."
  },
  {
    value: "+40%",
    label: "Conversion Rate",
    description: "Personalized AI ads turn viewers into buyers more effectively."
  },
  {
    value: "+30%",
    label: "CTR",
    description: "AI-optimized ads capture more attention than manually created ads."
  }
];

const UGC_SCRIPT_TAGS = ["[HOOK]", "[PROBLEM]", "[SOLUTION]", "[CTA]"];
const UGC_SCRIPT_PREVIEW =
  "[HOOK] Stop scrolling, this one change can boost your content results fast [PROBLEM] creators waste hours on scripts [SOLUTION] we generate ready-to-shoot UGC in seconds [CTA] try it now";
const VOICE_WAVE_HEIGHTS = [6, 10, 14, 20, 12, 18, 9, 16, 22, 11, 17, 8, 14, 6];

const DEFAULT_PRICING_CATALOG = {
  plans: [
    {
      slug: "starter",
      name: "Starter",
      billing_mode: "credits",
      price_cents: 3900,
      currency: "EUR",
      monthly_credits: 220,
      tagline: "Ideal for testing short UGC videos",
      cta_label: "Get started",
      marketing_features: [
        "220 credits per month",
        "Supports videos from 5 to 30 seconds",
        "Images, script, voice, and video generation included"
      ]
    },
    {
      slug: "basic",
      name: "Basic",
      billing_mode: "credits",
      price_cents: 7900,
      currency: "EUR",
      monthly_credits: 500,
      tagline: "Ideal for ongoing monthly content production",
      cta_label: "Get started",
      marketing_features: [
        "500 credits per month",
        "Supports videos from 5 to 30 seconds",
        "Images, script, voice, and video generation included"
      ]
    },
    {
      slug: "pro",
      name: "Pro",
      billing_mode: "credits",
      price_cents: 14900,
      currency: "EUR",
      monthly_credits: 1100,
      tagline: "Ideal for higher-volume video production",
      cta_label: "Get started",
      marketing_features: [
        "1100 credits per month",
        "Supports videos from 5 to 30 seconds",
        "Images, script, voice, and video generation included"
      ]
    },
    {
      slug: "agency",
      name: "Agency",
      billing_mode: "byo_api",
      price_cents: 0,
      currency: "EUR",
      monthly_credits: 0,
      eyebrow: "B2B / Private Label",
      tagline: "Want to use your own API keys?",
      description: "Use the platform with your own providers and unlimited usage.",
      cta_label: "Contact sales",
      contact_email: "hello@ugcpersona.com",
      marketing_features: [
        "Bring your own API keys",
        "White label ready",
        "Agency usage",
        "Custom limits",
        "No credit system"
      ]
    }
  ],
  credits_info: {
    operation_costs: {
      image: 1,
      copy: 0.5,
      tts: 1,
      tts_optimization: 0.5,
      video_per_second: 2
    },
    video_min_seconds: 5,
    video_max_seconds: 30
  }
};

const CREDIT_PLAN_BULLET_TONES = [
  "bg-gradient-to-br from-violet-300 via-fuchsia-400 to-cyan-300 shadow-[0_0_10px_rgba(168,85,247,.45)]",
  "bg-gradient-to-br from-sky-300 via-cyan-400 to-indigo-300 shadow-[0_0_10px_rgba(34,211,238,.4)]",
  "bg-gradient-to-br from-emerald-300 via-teal-400 to-cyan-300 shadow-[0_0_10px_rgba(45,212,191,.4)]"
];

function normalizePricingCatalog(pricingCatalog) {
  const incoming = pricingCatalog && typeof pricingCatalog === "object" ? pricingCatalog : {};
  return {
    plans: Array.isArray(incoming.plans) && incoming.plans.length ? incoming.plans : DEFAULT_PRICING_CATALOG.plans,
    creditsInfo: incoming.credits_info || DEFAULT_PRICING_CATALOG.credits_info
  };
}

function buildFaqItems(creditsInfo) {
  const costs = creditsInfo?.operation_costs || DEFAULT_PRICING_CATALOG.credits_info.operation_costs;
  const minSeconds = creditsInfo?.video_min_seconds || DEFAULT_PRICING_CATALOG.credits_info.video_min_seconds;
  const maxSeconds = creditsInfo?.video_max_seconds || DEFAULT_PRICING_CATALOG.credits_info.video_max_seconds;

  return [
    {
      q: "How do credits work?",
      a: [
        "Credits are used each time you generate content inside the platform.",
        "Video generation uses credits based on the number of seconds generated.",
        "Images, script, voice, and voice optimization also use credits."
      ]
    },
    {
      q: "How is video generation charged?",
      a: [
        "Video generation is charged based on the actual duration of the generated video.",
        "The system rounds the duration up to the next full second."
      ]
    },
    {
      q: "What does “rounded up” mean?",
      a: [
        "If your generated video lasts 7.2 seconds, it will be billed as 8 seconds.",
        "If it lasts 15.1 seconds, it will be billed as 16 seconds."
      ]
    },
    {
      q: "What video lengths are supported?",
      a: [`You can generate videos from about ${minSeconds} seconds up to ${maxSeconds} seconds.`]
    },
    {
      q: "How many credits does a video use?",
      a: [
        "The total depends on the final duration and the number of generation steps used.",
        "As a general rule, video generation uses:",
        `${costs.video_per_second} credits × billed seconds`,
        "Other steps like images, script, voice, and voice optimization also consume credits."
      ]
    },
    {
      q: "Why do retries sometimes use credits?",
      a: ["Credits are deducted only if the provider actually processes and bills the request."]
    },
    {
      q: "Do unused credits roll over?",
      a: ["No. Credits reset at each monthly renewal."]
    },
    {
      q: "Can I buy extra credits?",
      a: [
        "Not at the moment.",
        "Each plan includes a fixed monthly amount of credits."
      ]
    },
    {
      q: "What happens if I run out of credits?",
      a: ["You’ll need to wait for the next renewal or upgrade to a higher plan."]
    },
    {
      q: "Can I use my own API keys?",
      a: ["Yes. Agency customers can use a Bring Your Own API setup."]
    },
    {
      q: "Do you offer refunds?",
      a: [
        "Used credits are not refundable.",
        "Refunds are only considered in case of billing or technical errors."
      ]
    }
  ];
}

function formatPlanPrice(priceCents, currency = "EUR") {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: currency || "EUR",
    maximumFractionDigits: 0
  }).format((Number(priceCents) || 0) / 100);
}

function AccessButton({ auth, children, className, guestHref = "/users/sign_in" }) {
  if (auth?.id) {
    return (
      <Link href="/app" className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a href={guestHref} className={className}>
      {children}
    </a>
  );
}

function OverlayIcon({ className, children }) {
  return (
    <span className={cn("inline-flex h-6 w-6 items-center justify-center text-white", className)} aria-hidden>
      {children}
    </span>
  );
}

function IconHeart() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M12 21s-7.2-4.6-9.3-8.7C.8 8.7 3 4.8 7 4.8c2 0 3.4 1 5 2.9 1.6-1.9 3-2.9 5-2.9 4 0 6.2 3.9 4.3 7.5C19.2 16.4 12 21 12 21z" />
      </svg>
    </OverlayIcon>
  );
}

function IconComment() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M20 14.5c0 3.6-3.6 6.5-8 6.5-1.2 0-2.4-.2-3.4-.6L4 21.5l1.2-3C3.8 17.4 3 16 3 14.5 3 10.9 6.6 8 11 8s9 2.9 9 6.5z" />
      </svg>
    </OverlayIcon>
  );
}

function IconSend() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M21 3L10 14" />
        <path d="M21 3l-7 18-4-7-7-4 18-7z" />
      </svg>
    </OverlayIcon>
  );
}

function IconCamera() {
  return (
    <OverlayIcon className="h-7 w-7">
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-none stroke-current stroke-[1.8]">
        <path d="M4 8.5C4 7.1 5.1 6 6.5 6h2.4l1.3-1.5h3.6L15.1 6h2.4C18.9 6 20 7.1 20 8.5v8C20 17.9 18.9 19 17.5 19h-11C5.1 19 4 17.9 4 16.5v-8z" />
        <circle cx="12" cy="12.5" r="3.1" />
      </svg>
    </OverlayIcon>
  );
}

function IconHome() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M3 11.5L12 4l9 7.5" />
        <path d="M6 10.8V20h12v-9.2" />
      </svg>
    </OverlayIcon>
  );
}

function IconSearch() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l5 5" />
      </svg>
    </OverlayIcon>
  );
}

function IconReel() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.8]">
        <rect x="3.5" y="4.5" width="17" height="15" rx="4" />
        <path d="M8 4.5L11 9M13 4.5l3 4.5M3.8 9h16.4" />
        <path d="M10 11.5v4l4-2-4-2z" />
      </svg>
    </OverlayIcon>
  );
}

function IconBag() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.8]">
        <rect x="5" y="7" width="14" height="13" rx="3" />
        <path d="M9 9V7a3 3 0 016 0v2" />
      </svg>
    </OverlayIcon>
  );
}

function IconUser() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.8]">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c1.5-3 4-4.8 7-4.8s5.5 1.8 7 4.8" />
      </svg>
    </OverlayIcon>
  );
}

function IconClose() {
  return (
    <OverlayIcon className="h-7 w-7">
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-none stroke-current stroke-[1.9]">
        <path d="M5 5l14 14M19 5L5 19" />
      </svg>
    </OverlayIcon>
  );
}

function IconChevronUp() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M6 15l6-6 6 6" />
      </svg>
    </OverlayIcon>
  );
}

function IconDots() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
        <circle cx="6" cy="12" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="18" cy="12" r="1.7" />
      </svg>
    </OverlayIcon>
  );
}

function ReelsPreviewOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 text-white">
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/25" />

      <div className="absolute inset-x-4 top-10 flex items-center justify-between">
        <span className="text-4xl font-semibold tracking-tight">Reels</span>
        <IconCamera />
      </div>

      <div className="absolute bottom-28 right-3 flex flex-col items-center gap-3 text-center">
        <div className="space-y-0.5">
          <IconHeart />
          <p className="text-xs font-medium">823k</p>
        </div>
        <div className="space-y-0.5">
          <IconComment />
          <p className="text-xs font-medium">51</p>
        </div>
        <IconSend />
      </div>

      <div className="absolute bottom-16 left-3 right-14 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200/40 bg-emerald-500/25 text-[9px] font-semibold">
            LOGO
          </span>
          <span className="font-semibold">account_name</span>
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-black text-black">v</span>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-white/30 px-3 py-2 text-base backdrop-blur-sm">
          <span>Learn more</span>
          <span className="text-lg">&gt;</span>
        </div>
        <p className="truncate text-base">Brighten up your home with a green space ...</p>
        <div className="flex items-center justify-between text-sm text-white/85">
          <span>Sponsored</span>
          <IconDots />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-black/90 px-4 pb-2 pt-3">
        <div className="flex items-center justify-between">
          <IconHome />
          <IconSearch />
          <IconReel />
          <IconBag />
          <IconUser />
        </div>
      </div>
    </div>
  );
}

function StoriesPreviewOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 text-white">
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/25" />

      <div className="absolute inset-x-3 top-11 flex gap-1">
        <div className="h-1 rounded-full bg-white/95" style={{ width: "48%" }} />
        <div className="h-1 flex-1 rounded-full bg-white/45" />
      </div>

      <div className="absolute inset-x-3 top-14 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[9px] font-semibold text-slate-900">
            LOGO
          </span>
          <div className="leading-tight">
            <p className="text-[15px] font-semibold">account_name</p>
            <p className="text-[13px] text-white/85">Sponsored</p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-white/90">
          <IconDots />
          <IconClose />
        </div>
      </div>

      <div className="absolute left-1/2 top-[61%] -translate-x-1/2 rounded-xl border border-white/70 bg-white/90 px-5 py-2 text-[16px] font-semibold tracking-wide text-[#7d5547] shadow-lg">
        SHOP NOW
      </div>

      <div className="absolute inset-x-0 bottom-0">
        <div className="h-16 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-3 flex items-center justify-between px-10">
          <IconChevronUp />
          <IconSend />
        </div>
      </div>
    </div>
  );
}

function SocialPreviewOverlay({ mode }) {
  if (mode === "instagram_stories") return <StoriesPreviewOverlay />;
  return <ReelsPreviewOverlay />;
}

function BrandLogo({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="ugc-persona-logo-gradient" x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#22d3ee" />
          <stop offset="0.5" stopColor="#38bdf8" />
          <stop offset="1" stopColor="#34d399" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#020617" stroke="url(#ugc-persona-logo-gradient)" strokeWidth="3" />
      <path
        d="M17 45L30.5 17.5a2 2 0 013 0L47 45a2 2 0 01-1.8 2.9H18.8A2 2 0 0117 45z"
        fill="none"
        stroke="url(#ugc-persona-logo-gradient)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="33" r="5.5" fill="url(#ugc-persona-logo-gradient)" />
    </svg>
  );
}

function CreditsInfoTooltip({ creditsInfo }) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  function updatePosition() {
    if (!triggerRef.current || typeof window === "undefined") return;
    const rect = triggerRef.current.getBoundingClientRect();
    const maxWidth = Math.min(352, window.innerWidth - 24);
    const tooltipHeight = panelRef.current ? panelRef.current.getBoundingClientRect().height : 300;

    let left = rect.right - maxWidth;
    if (left < 12) left = 12;
    if (left + maxWidth > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - maxWidth - 12);
    }

    let top = rect.bottom + 10;
    if (top + tooltipHeight > window.innerHeight - 12) {
      top = Math.max(12, rect.top - tooltipHeight - 10);
    }

    setPosition({ left, top, width: maxWidth });
  }

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const raf = window.requestAnimationFrame(() => updatePosition());
    const handleReposition = () => updatePosition();
    const handleOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEsc);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <span className="relative z-[120] inline-flex shrink-0 align-middle">
      <button
        ref={triggerRef}
        type="button"
        aria-label="How credits work"
        aria-expanded={open}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-cyan-400/50 bg-cyan-500/10 text-[11px] font-bold text-cyan-200 transition hover:bg-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
        onClick={() => setOpen((prev) => !prev)}
      >
        i
      </button>
      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              className="pointer-events-auto fixed z-[9999] rounded-xl border border-slate-700 bg-slate-950/95 p-3 text-left text-xs leading-relaxed text-slate-200 shadow-[0_12px_30px_rgba(2,6,23,.55)] backdrop-blur whitespace-normal break-words"
              style={{
                left: `${position.left}px`,
                top: `${position.top}px`,
                width: `${position.width}px`,
                maxHeight: "70vh",
                overflowY: "auto"
              }}
            >
              <p className="font-semibold text-slate-100">How credits work</p>
              <p className="mt-1">Credits are used whenever you generate content inside the platform.</p>
              <p className="mt-2 font-semibold text-slate-100">Typical usage:</p>
              <p className="mt-1">Image generation -&gt; {creditsInfo?.operation_costs?.image ?? 1} credit</p>
              <p>Script generation -&gt; {creditsInfo?.operation_costs?.copy ?? 0.5} credits</p>
              <p>Voice generation -&gt; {creditsInfo?.operation_costs?.tts ?? 1} credit</p>
              <p>Voice optimization -&gt; {creditsInfo?.operation_costs?.tts_optimization ?? 0.5} credits</p>
              <p>Video generation -&gt; {creditsInfo?.operation_costs?.video_per_second ?? 2} credits per billed second *</p>
              <p className="mt-2">
                *Video duration is measured based on the final generated output and rounded up to the next full second.
              </p>
              <p className="mt-2">
                A typical 15-second video usually consumes around 35-40 credits, depending on retries and the number of assets generated.
              </p>
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

function socialLogo(platform) {
  if (platform === "Facebook") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#1877F2] text-base font-black text-white">
        f
      </span>
    );
  }

  if (platform === "Instagram") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] bg-gradient-to-br from-[#F9CE34] via-[#EE2A7B] to-[#6228D7]">
        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-white stroke-[1.9]" aria-hidden>
          <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="4.6" />
          <circle cx="12" cy="12" r="3.8" />
          <circle cx="17.2" cy="6.8" r="1" className="fill-white stroke-none" />
        </svg>
      </span>
    );
  }

  if (platform === "TikTok") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black text-sm font-black text-white ring-1 ring-slate-600">
        ♪
      </span>
    );
  }

  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] bg-[#0A66C2] text-[11px] font-black text-white">
      in
    </span>
  );
}

export default function HomeIndex({ auth, pricing_catalog: pricingCatalogProp }) {
  const influencerTopRow = INFLUENCER_GRID_IMAGES.slice(0, 4);
  const influencerBottomRow = INFLUENCER_GRID_IMAGES.slice(4, 8);
  const pricingCatalog = normalizePricingCatalog(pricingCatalogProp);
  const creditPlans = pricingCatalog.plans.filter((plan) => plan.billing_mode === "credits");
  const agencyPlans = pricingCatalog.plans.filter((plan) => plan.billing_mode === "byo_api");
  const faqItems = buildFaqItems(pricingCatalog.creditsInfo);
  const stepThreeBeamContainerRef = useRef(null);
  const stepThreeSourceRef = useRef(null);
  const stepThreeInstagramRef = useRef(null);
  const stepThreeFacebookRef = useRef(null);
  const stepThreeTiktokRef = useRef(null);
  const stepThreeLinkedinRef = useRef(null);

  return (
    <div className="min-h-screen bg-black text-white">
      <PublicNavbar auth={auth} />

      <main>
        <section className="relative overflow-hidden border-b border-slate-800 bg-black">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_5%,rgba(79,70,229,.55),transparent_46%),radial-gradient(circle_at_20%_18%,rgba(251,146,60,.35),transparent_38%),radial-gradient(circle_at_80%_14%,rgba(56,189,248,.4),transparent_38%)]" />
          <div className="relative mx-auto w-full max-w-[1140px] px-4 pb-10 pt-14 md:px-6 md:pb-14 md:pt-20">
            <div className="mx-auto max-w-2xl text-center">
              <h1 className="text-4xl font-semibold tracking-tight text-white md:text-6xl">Design your own VERY photorealistic AI influencer.</h1>
              <p className="mx-auto mt-4 max-w-xl text-sm text-slate-200 md:text-base">
                Generate authentic UGC videos effortlessly — from concept to ready-to-share content in minutes.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <ShimmerButton
                  type="button"
                  className="shadow-2xl"
                  onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  <span className="text-center text-sm font-medium leading-none tracking-tight text-white lg:text-lg">Create your Own Influencer</span>
                </ShimmerButton>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-6 md:px-6">
          <p className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.16em] text-violet-400">Creator Showcase</p>
          <div className="overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max snap-x snap-mandatory gap-3">
              {HERO_TILES.map((tile) => (
                <div
                  key={tile.id}
                  className="group relative h-[260px] w-[148px] shrink-0 snap-start overflow-hidden rounded-xl border border-slate-700 bg-slate-900 md:h-[320px] md:w-[180px]"
                >
                  <video
                    src={tile.src}
                    className="h-full w-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/65 to-transparent" />
                  <span className="pointer-events-none absolute bottom-2 right-2 rounded-full border border-white/30 bg-black/55 px-2 py-1 text-[10px] font-semibold text-white/90">
                    MUTE
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="mb-3 text-sm font-medium text-slate-300 md:text-base">
              Engage your audience across the world&apos;s most popular social platforms.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center md:grid-cols-4">
            {SOCIAL_PLATFORMS.map((platform) => (
              <div
                key={platform}
                className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-xs font-semibold tracking-wide text-slate-200"
              >
                {socialLogo(platform)}
                <span>{platform}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {RESULT_STATS.map((stat) => (
              <div key={stat.label} className="metric-card rounded-2xl border border-slate-800/90 bg-black px-6 py-8 text-center shadow-[0_10px_30px_rgba(2,6,23,.45)]">
                <div className="relative z-10">
                  <p className="text-5xl font-semibold tracking-tight text-indigo-400 md:text-6xl">{stat.value}</p>
                  <p className="mt-5 text-2xl font-semibold tracking-tight text-white md:text-3xl">{stat.label}</p>
                  <p className="mx-auto mt-4 max-w-[32ch] leading-snug text-slate-300">{stat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-10 md:px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight md:text-4xl">Build your lifelike influencer for your product or service.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-400 md:text-base">
            Use generative AI image and video to amaze followers and generate leads.
          </p>
          <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-950 p-4 shadow-[0_16px_40px_rgba(2,6,23,.5)] md:p-6">
            <div className="grid gap-4 md:grid-cols-[1.45fr_0.95fr] md:items-center">
              <div className="overflow-hidden rounded-xl border border-slate-800/80 bg-black/40 p-2">
                <Marquee pauseOnHover className="[--duration:34s] [--gap:0.75rem]">
                  {influencerTopRow.map((src, idx) => (
                    <img
                      key={`top-${src}`}
                      src={src}
                      alt={`Influencer sample ${idx + 1}`}
                      className="aspect-[9/16] w-20 rounded-lg border border-slate-700 object-cover md:w-24 lg:w-28"
                      loading="lazy"
                    />
                  ))}
                </Marquee>
                <Marquee reverse pauseOnHover className="mt-3 [--duration:34s] [--gap:0.75rem]">
                  {influencerBottomRow.map((src, idx) => (
                    <img
                      key={`bottom-${src}`}
                      src={src}
                      alt={`Influencer sample ${idx + 5}`}
                      className="aspect-[9/16] w-20 rounded-lg border border-slate-700 object-cover md:w-24 lg:w-28"
                      loading="lazy"
                    />
                  ))}
                </Marquee>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">UGC Persona</p>
                <h3 className="mt-2 text-2xl font-semibold tracking-tight">Effortless Influencer Creation</h3>
                <p className="mt-3 text-sm text-slate-300">
                  Easily create your influencer and place them in any setting with the outfit you prefer. You&apos;ll always achieve spectacular
                  results that boost success on your social channels.
                </p>
                {auth?.id ? (
                  <Link
                    href="/app"
                    className="mt-5 inline-flex w-full items-center justify-center rounded-2xl bg-violet-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-violet-500 sm:w-auto"
                  >
                    Open app
                  </Link>
                ) : (
                  <a
                    href="#pricing"
                    className="mt-5 inline-flex w-full items-center justify-center rounded-2xl bg-violet-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-violet-500 sm:w-auto"
                  >
                    View pricing
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-10 md:px-6">
          <div className="space-y-8 rounded-2xl border border-slate-800 bg-slate-950 p-5 shadow-[0_16px_40px_rgba(2,6,23,.5)]">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-violet-400 md:text-base">Social Preview Lab</p>
              <h3 className="mt-2 text-4xl font-semibold tracking-tight md:text-5xl">See your content inside real social previews.</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-300 md:text-lg">
                Validate how your UGC looks before publishing. Preview the same video in Instagram Reels and Stories layouts to fine-tune framing, CTA placement, and overall impact.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 md:items-start">
              <div className="flex flex-col items-center gap-2">
                <Iphone className="h-[600px] w-auto max-w-full md:h-[700px]">
                  <div className="relative h-full w-full">
                    <video
                      src={VIDEO_SOURCES[0]}
                      className="h-full w-full bg-black object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                    <SocialPreviewOverlay mode="instagram_reels" />
                  </div>
                </Iphone>
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Instagram Reels</p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <Iphone className="h-[600px] w-auto max-w-full md:h-[700px]">
                  <div className="relative h-full w-full">
                    <video
                      src={VIDEO_SOURCES[1]}
                      className="h-full w-full bg-black object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                    <SocialPreviewOverlay mode="instagram_stories" />
                  </div>
                </Iphone>
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Instagram Stories</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1140px] gap-6 px-4 py-8 md:px-6">
          <div className="grid gap-6 md:min-h-[26rem] md:grid-cols-2 md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">Step 1</p>
              <h3 className="mt-2 text-2xl font-semibold">Generate Your Influencer With Top AI Models</h3>
              <p className="mt-3 text-sm text-slate-400">
                Unlike other tools, you only answer a few simple questions about your business. UGC Persona builds a consistent influencer identity
                and places it in the right setting automatically.
              </p>
            </div>
            <div className="relative h-[24rem] overflow-hidden rounded-2xl border border-slate-800 bg-black/80 shadow-[0_16px_30px_rgba(2,6,23,.45)]">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-16 bg-gradient-to-r from-black/90 to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-16 bg-gradient-to-l from-black/90 to-transparent" />
              <Marquee pauseOnHover className="h-full items-center px-4 [--duration:24s] [--gap:0.85rem]">
                {INFLUENCER_GRID_IMAGES.map((src, index) => (
                  <article
                    key={`step-one-${src}`}
                    className={cn(
                      "group relative h-[20.5rem] w-[11.2rem] overflow-hidden rounded-2xl border border-slate-700/90 bg-slate-900/70 shadow-[0_12px_24px_rgba(2,6,23,.48)] transition-transform duration-500",
                      index % 2 === 0 ? "rotate-[0.8deg]" : "-rotate-[0.8deg]"
                    )}
                  >
                    <img
                      src={src}
                      alt={`Influencer gallery ${index + 1}`}
                      className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/45 to-transparent" />
                  </article>
                ))}
              </Marquee>
            </div>
          </div>

          <div className="grid gap-6 md:min-h-[26rem] md:grid-cols-2 md:items-center">
            <div className="order-2 min-h-[24rem] rounded-2xl border border-slate-800 bg-black/85 p-4 shadow-[0_16px_30px_rgba(2,6,23,.45)] md:order-1 md:min-h-[24rem]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-300">AI Text + Voice Pipeline</p>

              <div className="mt-3 grid items-center gap-2 rounded-xl border border-slate-700/90 bg-slate-950/70 p-2 text-[11px] md:grid-cols-[1fr_auto_1fr_auto_1fr]">
                <div className="rounded-lg border border-slate-700 bg-slate-900/80 px-2.5 py-2">
                  <p className="flex items-center gap-1.5 font-semibold text-slate-100">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <rect x="4" y="4" width="16" height="16" rx="2.2" />
                      <path d="M8 9h8M8 13h5" />
                    </svg>
                    URL / Content
                  </p>
                  <p className="mt-1 text-slate-400">Page link, text, or product brief.</p>
                </div>

                <span className="hidden text-slate-500 md:block">→</span>

                <div className="rounded-lg border border-cyan-700/70 bg-cyan-500/10 px-2.5 py-2">
                  <p className="flex items-center gap-1.5 font-semibold text-cyan-200">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M12 3l1.8 3.8L18 8.6l-3 2.9.7 4.2L12 13.8l-3.7 1.9.7-4.2-3-2.9 4.2-1.8L12 3z" />
                    </svg>
                    AI Generation
                  </p>
                  <p className="mt-1 text-cyan-100/80">Builds UGC script and speech pacing.</p>
                </div>

                <span className="hidden text-slate-500 md:block">→</span>

                <div className="rounded-lg border border-slate-700 bg-slate-900/80 px-2.5 py-2">
                  <p className="flex items-center gap-1.5 font-semibold text-slate-100">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M4 6h16v12H4z" />
                      <path d="M8 10h8M8 14h6" />
                    </svg>
                    Script + Audio
                  </p>
                  <p className="mt-1 text-slate-400">Voice-ready output for your influencer.</p>
                </div>
              </div>

              <p className="mt-3 rounded-xl border border-slate-700/90 bg-slate-900/80 px-3 py-3 font-mono text-sm leading-relaxed text-slate-200">
                {UGC_SCRIPT_PREVIEW}
              </p>

              <div className="mt-2 rounded-xl border border-slate-700/90 bg-slate-900/80 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-200">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M5 10v4h3l4 3V7l-4 3H5z" />
                      <path d="M16 9a4 4 0 010 6" />
                      <path d="M18.7 6.8a7.5 7.5 0 010 10.4" />
                    </svg>
                    AI Voice Preview
                  </p>
                  <div className="flex items-end gap-[3px]">
                    {VOICE_WAVE_HEIGHTS.map((height, index) => (
                      <span
                        key={`wave-${index}`}
                        className="waveform-bar"
                        style={{
                          height: `${height}px`,
                          animationDelay: `${index * 0.08}s`
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {UGC_SCRIPT_TAGS.map((tag) => (
                  <span key={tag} className="rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-slate-300">
                    {tag}
                  </span>
                ))}
                <span className="rounded-lg border border-emerald-700/70 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
                  TTS Optimized
                </span>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">Step 2</p>
              <h3 className="mt-2 text-2xl font-semibold">Create Script And Social Copy With AI</h3>
              <p className="mt-3 text-sm text-slate-400">
                Share your web page URL or paste your text. Our AI analyzes it to generate a script with exact timing for social platforms and also
                creates the right supporting copy for sponsored ads or organic reels.
              </p>
            </div>
          </div>

          <div className="grid gap-6 md:min-h-[26rem] md:grid-cols-2 md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">Step 3</p>
              <h3 className="mt-2 text-2xl font-semibold">Preview, Validate, And Download Your Final Video</h3>
              <p className="mt-3 text-sm text-slate-400">
                Watch the final result, check how it appears in Instagram Reels or Stories, and download it instantly so you can publish on your
                channels right away.
              </p>
            </div>
            <div ref={stepThreeBeamContainerRef} className="relative h-64 overflow-hidden rounded-2xl border border-slate-800 bg-black/90 shadow-[0_16px_30px_rgba(2,6,23,.45)] md:h-[24rem]">
              <div className="pointer-events-none absolute inset-x-0 top-1/2 z-0 h-px bg-slate-800/90" />

              <div
                ref={stepThreeSourceRef}
                className="absolute left-6 top-1/2 z-20 inline-flex h-20 w-20 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white text-slate-900 shadow-[0_10px_22px_rgba(0,0,0,.5)] md:h-24 md:w-24"
              >
                <svg viewBox="0 0 24 24" className="h-10 w-10 fill-none stroke-current stroke-[1.8]" aria-hidden>
                  <rect x="3.5" y="6.5" width="12.5" height="11" rx="2.2" />
                  <path d="M16 10l4-2.8v9.6L16 14z" />
                  <path d="M8.5 10.2v3.6l3.1-1.8-3.1-1.8z" className="fill-current stroke-none" />
                </svg>
              </div>

              <div className="absolute right-6 top-[18%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeInstagramRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#F9CE34] via-[#EE2A7B] to-[#6228D7] text-white md:h-11 md:w-11">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-white stroke-[1.9]" aria-hidden>
                    <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="4.6" />
                    <circle cx="12" cy="12" r="3.8" />
                    <circle cx="17.2" cy="6.8" r="1" className="fill-white stroke-none" />
                  </svg>
                </div>
              </div>

              <div className="absolute right-6 top-[41%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeFacebookRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1877F2] text-lg font-black text-white md:h-11 md:w-11">
                  f
                </div>
              </div>

              <div className="absolute right-6 top-[64%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeTiktokRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-black text-base font-black text-white md:h-11 md:w-11">
                  ♪
                </div>
              </div>

              <div className="absolute right-6 top-[86%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeLinkedinRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0A66C2] text-sm font-black text-white md:h-11 md:w-11">
                  in
                </div>
              </div>

              <AnimatedBeam containerRef={stepThreeBeamContainerRef} fromRef={stepThreeSourceRef} toRef={stepThreeInstagramRef} curvature={120} duration={3.2} />
              <AnimatedBeam containerRef={stepThreeBeamContainerRef} fromRef={stepThreeSourceRef} toRef={stepThreeFacebookRef} curvature={44} duration={3.4} delay={0.2} />
              <AnimatedBeam containerRef={stepThreeBeamContainerRef} fromRef={stepThreeSourceRef} toRef={stepThreeTiktokRef} curvature={-34} duration={3.7} delay={0.45} />
              <AnimatedBeam
                containerRef={stepThreeBeamContainerRef}
                fromRef={stepThreeSourceRef}
                toRef={stepThreeLinkedinRef}
                curvature={-106}
                duration={4}
                delay={0.7}
                reverse
              />
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden border-y border-slate-800 bg-slate-950 py-12 text-white">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,.45),transparent_45%),radial-gradient(circle_at_80%_80%,rgba(236,72,153,.35),transparent_40%)]" />
          <div className="relative mx-auto w-full max-w-[1140px] px-4 md:px-6">
            <h2 className="text-center text-3xl font-semibold tracking-tight">Lead & Revenue Impact</h2>
            <p className="mx-auto mt-2 max-w-3xl text-center text-sm text-slate-200/90">
              Publishing UGC content consistently across organic channels and paid ads helps brands generate more qualified leads, increase sales,
              and boost brand awareness.
            </p>
            <div className="mt-8 grid gap-3 md:grid-cols-4">
              {[
                {
                  label: "Qualified leads",
                  value: "+320%",
                  description: "More inbound leads from always-on organic reels plus retargeting ads."
                },
                {
                  label: "Ad-to-lead conversion",
                  value: "2.4x",
                  description: "UGC-native creatives convert better than static ads in paid campaigns."
                },
                {
                  label: "Organic reach",
                  value: "+68%",
                  description: "Short-form content cadence improves visibility and audience discovery."
                },
                {
                  label: "Brand awareness",
                  value: "+41%",
                  description: "Higher branded searches and stronger recall after multichannel distribution."
                }
              ].map((item) => (
                <div key={item.label} className="rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.14em] text-cyan-200">{item.label}</p>
                  <p className="mt-3 text-3xl font-semibold">{item.value}</p>
                  <p className="mt-1 text-sm text-slate-200">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="mx-auto w-full max-w-[1140px] px-4 py-12 md:px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight md:text-4xl">Pricing</h2>
          <p className="mx-auto mt-3 max-w-3xl text-center text-base text-slate-300 md:text-lg">
            Choose the plan that matches how many final UGC videos you want to produce each month.
          </p>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {creditPlans.map((plan, index) => (
              <MagicCard
                key={plan.slug}
                allowOverflow
                className={cn("pricing-card flex h-full flex-col p-7 md:p-8", plan.slug !== "starter" && plan.popular && "pricing-card-popular")}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-100">{plan.name}</p>
                  {plan.slug !== "starter" && plan.badge_label ? <span className="pricing-chip">{plan.badge_label}</span> : null}
                </div>
                <p className="mt-5 text-3xl font-semibold tracking-tight md:text-4xl">
                  {formatPlanPrice(plan.price_cents, plan.currency)} <span className="text-[0.68em] font-medium text-slate-300">/ month</span>
                </p>
                {plan.tagline ? <p className="mt-4 text-sm text-slate-300">{plan.tagline}</p> : null}
                <ul className="mt-8 space-y-3.5 text-sm text-slate-300 md:text-[1.02rem]">
                  {plan.marketing_features.map((item, idx) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className={cn("mt-2 h-2 w-2 shrink-0 rounded-full", CREDIT_PLAN_BULLET_TONES[index % CREDIT_PLAN_BULLET_TONES.length])} />
                      <div className="inline-flex items-center gap-2">
                        <span>{item}</span>
                        {idx === 0 ? <CreditsInfoTooltip creditsInfo={pricingCatalog.creditsInfo} /> : null}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-10">
                  <AccessButton
                    auth={auth}
                    guestHref={`/users/sign_up?plan=${encodeURIComponent(plan.slug)}`}
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-violet-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-violet-500"
                  >
                    {auth?.id ? "Open app" : plan.cta_label || "Create account"}
                  </AccessButton>
                </div>
              </MagicCard>
            ))}
          </div>

          {agencyPlans.map((plan) => (
            <div key={plan.slug} className="mx-auto mt-6 max-w-[34rem]">
              <MagicCard allowOverflow className="pricing-card flex h-full flex-col p-7 text-center md:p-8">
                <p className="text-sm font-semibold text-cyan-200">{plan.eyebrow || plan.name}</p>
                <p className="mt-5 text-[1.75rem] font-semibold leading-snug text-slate-100">{plan.tagline || plan.name}</p>
                {plan.description ? <p className="mt-3 text-base leading-relaxed text-slate-300">{plan.description}</p> : null}
                <ul className="mt-8 space-y-3.5 text-sm text-slate-300 md:text-[1.02rem]">
                  {plan.marketing_features.map((item) => (
                    <li key={item} className="flex items-start justify-center gap-3 text-left">
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-cyan-300 via-sky-400 to-teal-300 shadow-[0_0_10px_rgba(34,211,238,.42)]" />
                      <span className="min-w-[16rem]">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-10">
                  <a
                    href={`mailto:${plan.contact_email || "support@ugcpersona.com"}?subject=UGC%20Persona%20-%20B2B%20Inquiry`}
                    className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-600 bg-slate-900 px-4 py-3 text-base font-semibold text-slate-100 transition hover:border-slate-400 hover:bg-slate-800"
                  >
                    {plan.cta_label || "Contact sales"}
                  </a>
                </div>
              </MagicCard>
            </div>
          ))}
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-12 md:px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight md:text-4xl">FAQ</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-400 md:text-base">
            The most common questions about credits, billing logic, retries, and agency usage.
          </p>
          <div className="mx-auto mt-8 grid max-w-5xl gap-4 md:grid-cols-2">
            {faqItems.map((item) => (
              <details key={item.q} className="group h-fit rounded-xl border border-slate-800 bg-slate-950 p-4 shadow-[0_8px_24px_rgba(2,6,23,.35)]">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-100 marker:content-['']">
                  <span className="flex-1">{item.q}</span>
                  <span className="shrink-0 pt-0.5 text-slate-400 transition group-open:rotate-45">+</span>
                </summary>
                <div className="mt-3 space-y-2 text-sm leading-relaxed text-slate-300">
                  {item.a.map((line, index) => (
                    <p key={`${item.q}-${index}`}>{line}</p>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 pb-14 md:px-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6 text-center shadow-[0_12px_30px_rgba(2,6,23,.5)] md:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-300">Support</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight">Have questions? We are here to help.</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-300">
              We help you configure workflows, APIs, and models to get the best out of your UGC content, from the initial concept to the final
              publish-ready video.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a
                href="mailto:hello@ugcpersona.com"
                className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
              >
                Contact support
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-800 bg-slate-950 py-10 text-slate-300">
        <div className="mx-auto flex w-full max-w-[1140px] flex-col items-center px-4 text-center md:px-6">
          <div className="flex items-center gap-2">
            <BrandLogo className="h-8 w-8" />
            <p className="text-base font-semibold tracking-tight text-white">UGC Persona</p>
          </div>
          <p className="mt-1 text-xs text-slate-400">Your AI influencer. Your brand. Infinite content.</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-400">
            <Link href="/docs" className="transition hover:text-white">
              Docs
            </Link>
            <Link href="/terms" className="transition hover:text-white">
              Terms & Conditions
            </Link>
            <Link href="/privacy-cookies" className="transition hover:text-white">
              Privacy & Cookies
            </Link>
            <a href="mailto:hello@ugcpersona.com" className="transition hover:text-white">
              Contact
            </a>
          </div>
          <p className="mt-3 text-xs text-slate-500">© {new Date().getFullYear()} UGC Persona. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
